import { createPortal } from "react-dom"
import { useEffect, useState, useMemo } from "react"
import { langStore } from "@/stores/lang-store"
import { formatMessage } from "@/utils/format-message"
import CloseIcon from "@/components/icons/close-icon"
import LeakHeatmapCalendar from "./leak-heatmap-calendar"
import LeakMonthDetail from "./leak-month-detail"
import PeriodSettingMobile from "./period-setting-mobile"
import { analysisBundle } from "@/routes/apt/[apt_code]/analysis/analysis-const"
import { modalBundle } from "./detail-modal-const"

// [수정] modalBundle, extra 가져오기
const LEAK_START_TIME_FILTER = {
  key: "leakStartTime",
  label: { ko: "누수시작시점", en: "Leak Start Time" }
}

const DATE_FILTERS = [
  { key: "week", label: { ko: "1주일", en: "1W" } },
  { key: "months", label: { ko: "1개월", en: "1M" } },
  { key: "3months", label: { ko: "3개월", en: "3M" } },
  { key: "6months", label: { ko: "6개월", en: "6M" } },
  { key: "years", label: { ko: "1년", en: "1Y" } }
]

const leakChartBundle = {
  title: { ko: "시간당 물 사용량", en: "Hourly Water Usage" },
  leakStartTimeFilter: LEAK_START_TIME_FILTER,
  dateFilters: DATE_FILTERS,
  filter: {
    periodSettings: { ko: "기간 설정", en: "Period Settings" },
    startDate: { ko: "시작일", en: "Start Date" },
    endDate: { ko: "종료일", en: "End Date" }
  },
  noSelectionMessage: {
    ko: "* 동 호수 선택 시 기간이 나타납니다.",
    en: "* Select a unit to view the period."
  },
  customRangeLabel: {
    ko: "직접 설정",
    en: "Custom Range"
  },
  time: { ko: "시", en: "H" },
  search: { ko: "검색", en: "Search" },
  info: {
    usage: { ko: "사용량", en: "Usage" },
    leakAmount: { ko: "누수량", en: "Leak Volume" },
    leakRate: { ko: "누수율", en: "Leak Rate" },
    leakStartTime: { ko: "누수시작시점", en: "Leak Start Time" },
    leakType: { ko: "누수 유형", en: "Leak Type" },
    period: { ko: "기간", en: "period" }
  }
}

const AptLeakDetailModal = ({ isOpen, onClose, selectedUnit, chartData, periodProps }) => {
  const { lang } = langStore()

  const [selectedMonthDate, setSelectedMonthDate] = useState(null)

  // 화면 크기가 PC(768px 이상)로 변경되면 모달 닫기
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768 && isOpen) {
        onClose()
      }
    }
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [isOpen, onClose])

  // [수정] 차트 데이터 가공: 누수율 기준 히트맵 데이터 생성
  const { dailyData, maxDailyVal } = useMemo(() => {
    // 데이터가 없으면 초기값 리턴
    if (!chartData || !chartData.series || !chartData.xLabels) {
      return { dailyData: {}, maxDailyVal: 0 }
    }

    const { series, xLabels } = chartData

    // 시리즈 찾기
    const usageSeries =
      series.find((s) => s.name && (s.name.includes("사용") || s.name.includes("Usage"))) ||
      series[0]
    // 누수 시리즈 찾기 (보통 시리즈 이름에 '누수'나 'Leak'이 포함됨, 없으면 두번째 시리즈 가정)
    const leakSeries =
      series.find((s) => s.name && (s.name.includes("누수") || s.name.includes("Leak"))) ||
      series[1]

    const usageValues = usageSeries?.data || []
    const leakValues = leakSeries?.data || []

    // 일별 합계 임시 저장소
    const tempUsage = {}
    const tempLeak = {}

    // 1. 시간별 데이터를 일별로 합산
    xLabels.forEach((label, idx) => {
      const dateKey = label.substring(0, 10)
      const uVal = Number(usageValues[idx]) || 0
      const lVal = Number(leakValues[idx]) || 0

      if (!tempUsage[dateKey]) {
        tempUsage[dateKey] = 0
        tempLeak[dateKey] = 0
      }

      tempUsage[dateKey] += uVal
      tempLeak[dateKey] += lVal
    })

    // 2. 일별 누수율 계산 ( (누수량 / 사용량) * 100 )
    const rateMap = {}
    let maxVal = 0

    Object.keys(tempUsage).forEach((key) => {
      const dayUsage = tempUsage[key]
      const dayLeak = tempLeak[key]
      let rate = 0

      if (dayUsage > 0) {
        rate = (dayLeak / dayUsage) * 100
      } else if (dayLeak > 0) {
        rate = 100
      }

      rateMap[key] = rate

      if (rate > maxVal) maxVal = rate
    })

    return { dailyData: rateMap, maxDailyVal: maxVal }
  }, [chartData])

  if (!isOpen || !selectedUnit) return null
  // 데이터 매핑
  const leakInfoRow = {
    leak_start_at: selectedUnit.leakStartAt + modalBundle.hours[lang],
    leak_volume: `${selectedUnit.leakAmount} m³`,
    usage_volume: `${selectedUnit.usageAmount} m³`,
    leak_rate: `${selectedUnit.leakRate} %`,
    leak_type: selectedUnit.leakType
  }

  return createPortal(
    <section className="fixed h-screen overflow-hidden inset-0 z-9999 flex items-start md:items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      <div className="relative bg-white md:rounded-xl w-full h-full md:h-auto md:min-w-5xl md:w-6xl shadow-xl overflow-hidden flex flex-col">
        <div className="block w-full h-full relative overflow-hidden bg-white">
          <div
            className={`
              flex w-[200%] h-full transition-transform duration-300 ease-in-out
              ${selectedMonthDate ? "-translate-x-1/2" : "translate-x-0"}
            `}
          >
            {/* [Slide 1] 메인 상세 정보 화면 (왼쪽) */}
            <div className="w-1/2 h-full overflow-y-auto p-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">
                  {formatMessage(analysisBundle.dongho[lang], {
                    dong: selectedUnit.dong,
                    ho: selectedUnit.ho
                  })}
                </h2>
                <button className="p-2 bg-gray-100 rounded-md" onClick={onClose}>
                  <CloseIcon />
                </button>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 space-y-3 mb-6">
                <h3 className="font-bold text-gray-800 mb-3 text-sm pb-2 border-b border-gray-200">
                  {modalBundle.infoHeader.title[lang]}
                </h3>

                {modalBundle.infoHeader.rows.map((row) => {
                  if (row.key === "leak_severity") return null
                  return (
                    <div
                      key={row.key}
                      className="flex justify-between border-b border-gray-200 last:border-0 pb-2 last:pb-0"
                    >
                      <span className="text-gray-500">{row.label[lang]}</span>
                      <span className="font-semibold text-right">{leakInfoRow[row.key]}</span>
                    </div>
                  )
                })}
              </div>

              <div className="mt-6 pb-4">
                <h3 className="font-semibold text-lg text-left mb-2">
                  {leakChartBundle.title[lang]}
                </h3>

                {periodProps && (
                  <PeriodSettingMobile
                    bundle={leakChartBundle}
                    onPeriodSearch={periodProps.onSearch}
                  />
                )}

                <div className="min-h-60 mb-6">
                  <LeakHeatmapCalendar
                    startDate={periodProps?.startDate}
                    endDate={periodProps?.endDate}
                    dailyData={dailyData}
                    maxVal={maxDailyVal}
                    onMonthClick={(date) => setSelectedMonthDate(date)}
                  />
                </div>
              </div>
            </div>

            {/* [Slide 2] 월별 상세 화면 */}
            <div className="w-1/2 h-full overflow-y-auto p-4 bg-white">
              <LeakMonthDetail
                date={selectedMonthDate}
                onBack={() => setSelectedMonthDate(null)}
                dailyData={dailyData}
                maxVal={maxDailyVal}
                series={chartData?.series}
                xLabels={chartData?.xLabels}
              />
            </div>
          </div>
        </div>
      </div>
    </section>,
    document.body
  )
}

export default AptLeakDetailModal
